import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BoardRow, ProjectBoardGroup, ReviewStatus } from '@katsquare/types';
import { Button, Spinner } from '../components/ui';
import {
  createIssue,
  deleteIssue,
  deleteProof,
  getProof,
  getProject,
  getProjectBoard,
  moveProof,
  renameIssue,
  renameProof,
  restoreItem,
  uploadProof,
} from '../lib/api';
import { useFileDrop } from '../lib/useFileDrop';
import { showUploadError } from '../lib/uploadError';
import { useToast } from '../components/Toast';
import { usePlanLimit } from '../components/PlanLimitDialog';
import { useCanEdit } from '../features/auth/useWorkspaceRole';
import { IconComment, IconUpload } from '../components/icons';

type SectionSort = 'updated' | 'name';
type Month = { id: string; name: string };

export function ProjectPage() {
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();

  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => getProject(projectId) });
  const board = useQuery({
    queryKey: ['projectBoard', projectId],
    queryFn: () => getProjectBoard(projectId),
    // Keep polling (even when the tab is unfocused) while anything is still
    // rendering, then stop.
    refetchInterval: (q) =>
      q.state.data?.some((g) => g.rows.some((r) => r.renderStatus && r.renderStatus !== 'READY' && r.renderStatus !== 'FAILED'))
        ? 3000
        : false,
    refetchIntervalInBackground: true,
  });

  const addIssue = useMutation({
    mutationFn: (name: string) => createIssue(projectId, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projectBoard', projectId] }),
  });
  const [newIssue, setNewIssue] = useState(false);
  const [issueName, setIssueName] = useState('');
  // The route element is reused when only :projectId changes — close and clear
  // the new-issue form so magazine A's draft never lands on magazine B.
  useEffect(() => {
    setNewIssue(false);
    setIssueName('');
  }, [projectId]);
  // Keep the example current so it never reads as stale (e.g. "March 2027").
  const now = new Date();
  const issuePlaceholder = `Issue name (e.g. ${now.toLocaleString('default', {
    month: 'long',
  })} ${now.getFullYear()})`;
  const [sort, setSort] = useState<SectionSort>('updated');
  const canEdit = useCanEdit(project.data?.workspaceId);

  // Clicking a month in the sidebar links here with ?issue=<id> — scroll to it.
  const [sp] = useSearchParams();
  const issueParam = sp.get('issue');
  useEffect(() => {
    if (!issueParam) return;
    document.getElementById(`issue-${issueParam}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [issueParam, board.data]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex items-center justify-between gap-3 border-b border-neutral-200 bg-white px-6 py-3.5">
        <h1 className="text-lg font-semibold tracking-tight">{project.data?.name ?? 'Project'}</h1>
        <div className="flex items-center gap-2">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SectionSort)}
            title="Sort PDFs"
            className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs text-neutral-600 outline-none focus:border-neutral-400"
          >
            <option value="updated">Recently updated</option>
            <option value="name">Name (A–Z)</option>
          </select>
          {canEdit && (
            <Button variant="ghost" onClick={() => setNewIssue((v) => !v)} className="text-sm">
              + New issue
            </Button>
          )}
        </div>
      </header>

      {newIssue && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setNewIssue(false);
              setIssueName('');
            }}
          />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const n = issueName.trim();
              if (n) addIssue.mutate(n);
              setIssueName('');
              setNewIssue(false);
            }}
            className="relative z-20 flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-6 py-2"
          >
          <input
            autoFocus
            value={issueName}
            onChange={(e) => setIssueName(e.target.value)}
            placeholder={issuePlaceholder}
            maxLength={200}
            className="w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm outline-none focus:border-neutral-900 sm:w-64"
          />
          <Button type="submit" disabled={addIssue.isPending} className="text-sm">
            Create
          </Button>
          </form>
        </>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {board.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : board.data?.length === 0 ? (
          <p className="py-16 text-center text-sm text-neutral-400">
            No issues yet. Create one to start uploading proofs.
          </p>
        ) : (
          <div className="mx-auto max-w-5xl space-y-6">
            {board.data?.map((group) => (
              <IssueGroup
                key={group.issue.id}
                group={group}
                projectId={projectId}
                sort={sort}
                canEdit={canEdit}
                months={board.data!.map((g) => ({ id: g.issue.id, name: g.issue.name }))}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function IssueGroup({
  group,
  projectId,
  sort,
  canEdit,
  months,
}: {
  group: ProjectBoardGroup;
  projectId: string;
  sort: SectionSort;
  canEdit: boolean;
  months: Month[];
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(true);
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(group.issue.name);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['projectBoard', projectId] });

  const toast = useToast();
  const navigate = useNavigate();
  const showPlanLimit = usePlanLimit();
  const upload = useMutation({
    mutationFn: (file: File) => uploadProof(group.issue.id, file),
    onSuccess: invalidate,
    onError: (e) => showUploadError(e, { toast, showPlanLimit, onUpgrade: () => navigate('/pricing') }),
  });
  const rename = useMutation({
    mutationFn: () => renameIssue(group.issue.id, name.trim()),
    onSuccess: () => {
      setRenaming(false);
      invalidate();
    },
    onError: (e: Error) => toast(e.message),
  });
  const del = useMutation({
    mutationFn: () => deleteIssue(group.issue.id),
    onSuccess: () => {
      invalidate();
      toast(`Month “${group.issue.name}” moved to recycle bin`, {
        label: 'Undo',
        onClick: () => restoreItem('month', group.issue.id).then(invalidate),
      });
    },
    onError: (e: Error) => toast(e.message),
  });
  const drop = useFileDrop((files) => files.forEach((f) => upload.mutate(f)));
  const isOver = canEdit && drop.isOver;
  const dropHandlers = canEdit ? drop.dropHandlers : {};

  return (
    <section
      id={`issue-${group.issue.id}`}
      {...dropHandlers}
      className={`relative scroll-mt-4 rounded-xl border bg-white transition-colors ${
        isOver ? 'border-blue-500 ring-2 ring-blue-200' : 'border-neutral-200'
      }`}
    >
      {isOver && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-blue-50/80 text-sm font-medium text-blue-700">
          Drop to add PDF(s) to {group.issue.name}
        </div>
      )}
      <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-2.5">
        {renaming ? (
          <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setName(group.issue.name);
              setRenaming(false);
            }}
          />
          <form
            className="relative z-20 flex flex-1 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) rename.mutate();
            }}
          >
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setName(group.issue.name);
                  setRenaming(false);
                }
              }}
              maxLength={200}
              className="w-full rounded-md border border-neutral-300 px-3 py-1 text-sm outline-none focus:border-neutral-900 sm:w-64"
            />
            <Button type="submit" disabled={rename.isPending} className="text-sm">
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="text-sm"
              onClick={() => {
                setName(group.issue.name);
                setRenaming(false);
              }}
            >
              Cancel
            </Button>
          </form>
          </>
        ) : (
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left">
            <span className="text-neutral-400">{open ? '▾' : '▸'}</span>
            <span className="font-semibold text-neutral-800">{group.issue.name}</span>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
              {group.rows.length}
            </span>
          </button>
        )}
        {!renaming && canEdit && (
          <div className="flex items-center gap-1">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
            <Button variant="ghost" onClick={() => fileRef.current?.click()} disabled={upload.isPending} className="flex items-center gap-1.5 text-sm">
              {upload.isPending ? <Spinner /> : <IconUpload className="h-4 w-4" />}
              Upload PDF
            </Button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenu((m) => !m)}
                title="Month options"
                aria-expanded={menu}
                className="flex h-7 w-7 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 aria-expanded:bg-neutral-100"
              >
                ⋮
              </button>
              {menu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
                  <div className="absolute right-0 top-8 z-20 w-32 overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setMenu(false);
                        setName(group.issue.name);
                        setRenaming(true);
                      }}
                      className="block w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100"
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenu(false);
                        setConfirming(true);
                      }}
                      className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {confirming && (
        <div className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
          <span className="mr-auto">Delete “{group.issue.name}” and all its PDFs?</span>
          <Button
            type="button"
            variant="danger"
            onClick={() => del.mutate()}
            disabled={del.isPending}
            className="px-2 py-1 text-xs"
          >
            {del.isPending ? <Spinner /> : 'Delete'}
          </Button>
          <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      )}

      {open && (
        <ul>
          {group.rows.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-neutral-400">No PDFs yet.</li>
          ) : (
            [...group.rows]
              .sort((a, b) =>
                sort === 'name'
                  ? a.proofName.localeCompare(b.proofName)
                  : b.updatedAt.localeCompare(a.updatedAt),
              )
              .map((row) => (
                <ProofListRow
                  key={row.proofId}
                  row={row}
                  projectId={projectId}
                  currentIssueId={group.issue.id}
                  months={months}
                  canEdit={canEdit}
                />
              ))
          )}
        </ul>
      )}
    </section>
  );
}

/** Compact version switcher on a board row — opens that version in review. */
function RowVersionPicker({ proofId, latestNumber }: { proofId: string; latestNumber: number }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const proof = useQuery({ queryKey: ['proof', proofId], queryFn: () => getProof(proofId), enabled: open });
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title="Versions"
        className="flex items-center gap-0.5 rounded border border-neutral-200 px-1.5 py-0.5 text-xs text-neutral-500 hover:bg-neutral-100"
      >
        v{latestNumber}
        <span className="text-neutral-400">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-7 z-20 max-h-56 w-36 overflow-y-auto rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
            {proof.isPending ? (
              <div className="px-3 py-1.5 text-xs text-neutral-400">Loading…</div>
            ) : (
              [...(proof.data?.versions ?? [])]
                .sort((a, b) => b.number - a.number)
                .map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => navigate(`/pdfs/${proofId}?v=${v.id}`)}
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-neutral-100"
                  >
                    Version {v.number}
                  </button>
                ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ProofListRow({
  row,
  projectId,
  currentIssueId,
  months,
  canEdit,
}: {
  row: BoardRow;
  projectId: string;
  currentIssueId: string;
  months: Month[];
  canEdit: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [moveMenu, setMoveMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [name, setName] = useState(row.proofName);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['projectBoard', projectId] });

  const toast = useToast();
  const rename = useMutation({
    mutationFn: () => renameProof(row.proofId, name.trim()),
    onSuccess: () => {
      setRenaming(false);
      invalidate();
    },
    onError: (e: Error) => toast(e.message),
  });
  const move = useMutation({
    mutationFn: (issueId: string) => moveProof(row.proofId, issueId),
    onSuccess: () => {
      setMenu(false);
      setMoveMenu(false);
      invalidate();
    },
    onError: (e: Error) => toast(e.message),
  });
  const del = useMutation({
    mutationFn: () => deleteProof(row.proofId),
    onSuccess: () => {
      invalidate();
      toast(`PDF “${row.proofName}” moved to recycle bin`, {
        label: 'Undo',
        onClick: () => restoreItem('section', row.proofId).then(invalidate),
      });
    },
    onError: (e: Error) => toast(e.message),
  });
  const otherMonths = months.filter((m) => m.id !== currentIssueId);

  if (renaming) {
    return (
      <li className="border-t border-neutral-100 px-4 py-3">
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setName(row.proofName);
            setRenaming(false);
          }}
        />
        <form
          className="relative z-20 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) rename.mutate();
          }}
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setName(row.proofName);
                setRenaming(false);
              }
            }}
            maxLength={200}
            className="flex-1 rounded-md border border-neutral-300 px-3 py-1.5 text-sm outline-none focus:border-neutral-900"
          />
          <Button type="submit" disabled={rename.isPending} className="text-sm">
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="text-sm"
            onClick={() => {
              setName(row.proofName);
              setRenaming(false);
            }}
          >
            Cancel
          </Button>
        </form>
      </li>
    );
  }

  const approved = row.reviewStatus === 'APPROVED';
  return (
    <li className={`group relative border-t border-neutral-100 ${approved ? 'border-l-2 border-l-emerald-400' : ''}`}>
      <div
        className={`flex items-center gap-4 px-4 py-3 transition-colors ${
          approved ? 'bg-emerald-50/60 hover:bg-emerald-50' : 'hover:bg-neutral-50'
        }`}
      >
        <button
          type="button"
          onClick={() => navigate(`/pdfs/${row.proofId}`)}
          className="flex min-w-0 flex-1 items-center gap-4 text-left"
        >
          <div className="h-14 w-20 shrink-0 overflow-hidden rounded border border-neutral-200 bg-neutral-100">
            {row.thumbnailUrl ? (
              <img src={row.thumbnailUrl} alt="" className="h-full w-full object-cover object-top" loading="lazy" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[10px] text-neutral-400">
                {row.renderStatus === 'FAILED' ? 'failed' : '…'}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-neutral-900">{row.proofName}</p>
          </div>
        </button>

        {row.openComments > 0 && (
          <span className="hidden items-center gap-1 text-xs text-neutral-400 sm:flex" title="Open comments">
            <IconComment className="h-3.5 w-3.5" />
            {row.openComments}
          </span>
        )}
        {row.versionNumber != null && (
          <div className="hidden sm:block">
            <RowVersionPicker proofId={row.proofId} latestNumber={row.versionNumber} />
          </div>
        )}
        <div className="w-auto text-right sm:w-36">
          <StatusText row={row} />
        </div>

        {canEdit && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenu((m) => !m)}
            className="flex h-7 w-7 items-center justify-center rounded text-neutral-400 opacity-100 hover:bg-neutral-200 md:opacity-0 md:group-hover:opacity-100 aria-expanded:opacity-100"
            aria-expanded={menu}
            title="PDF options"
          >
            ⋮
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-0 top-8 z-20 w-40 overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setMenu(false);
                    setName(row.proofName);
                    setRenaming(true);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100"
                >
                  Rename
                </button>
                {otherMonths.length > 0 && (
                  <div
                    className="relative"
                    onMouseEnter={() => setMoveMenu(true)}
                    onMouseLeave={() => setMoveMenu(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setMoveMenu((m) => !m)}
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-neutral-100"
                    >
                      Move to… <span className="text-neutral-400">▸</span>
                    </button>
                    {moveMenu && (
                      <div className="absolute left-0 top-full mt-1 max-h-56 w-40 overflow-y-auto rounded-md border border-neutral-200 bg-white py-1 shadow-lg sm:left-auto sm:right-full sm:top-0 sm:mt-0 sm:mr-1">
                        {otherMonths.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => move.mutate(m.id)}
                            disabled={move.isPending}
                            className="block w-full truncate px-3 py-1.5 text-left text-sm hover:bg-neutral-100"
                          >
                            {m.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMenu(false);
                    setConfirming(true);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {confirming && (
        <div className="flex items-center justify-end gap-2 bg-red-50 px-4 py-2 text-xs text-red-700">
          <span className="mr-auto">Delete “{row.proofName}” and all its versions?</span>
          <Button
            type="button"
            variant="danger"
            onClick={() => del.mutate()}
            disabled={del.isPending}
            className="px-2 py-1 text-xs"
          >
            {del.isPending ? <Spinner /> : 'Delete'}
          </Button>
          <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      )}
    </li>
  );
}

const REVIEW_LABEL: Record<ReviewStatus, { label: string; cls: string }> = {
  NO_GATES: { label: 'In review', cls: 'text-amber-600' },
  IN_REVIEW: { label: 'In review', cls: 'text-amber-600' },
  CHANGES_REQUESTED: { label: 'In review', cls: 'text-amber-600' },
  APPROVED: { label: 'Approved', cls: 'text-emerald-600 font-medium' },
};

function StatusText({ row }: { row: BoardRow }) {
  if (row.renderStatus && row.renderStatus !== 'READY') {
    const label = row.renderStatus === 'FAILED' ? 'Render failed' : 'Rendering…';
    return <span className={`text-sm ${row.renderStatus === 'FAILED' ? 'text-red-600' : 'text-neutral-400'}`}>{label}</span>;
  }
  if (row.reviewStatus) {
    const s = REVIEW_LABEL[row.reviewStatus];
    if (row.reviewStatus === 'APPROVED') {
      return (
        <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
          ✓ Approved
        </span>
      );
    }
    return (
      <span className="rounded bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600">{s.label}</span>
    );
  }
  return <span className="text-sm text-neutral-400">—</span>;
}
